import { Injectable } from '@angular/core';

import { DialogService } from 'dspx-core';

import { API_ENDPOINTS } from '../constants/api-endpoints.constants';
import { LUXON_CELL_FORMAT, DATA_TYPE, EXCEL_CELL_FORMAT, EXCEL_CELL_TYPE } from '../constants/import-export.constants';

import { ExportToDataTablesRequest } from '../interfaces/export-to-data-tables-request.interface';
import { ExportToDataTablesResponse } from '../interfaces/export-to-data-tables-response.interface';
import { DataTable } from '../interfaces/data-table.interface';
import { ExportRequest } from '../interfaces/export-request.interface';
import { ExportSummaryReportRequest } from '../interfaces/export-summary-report-request.interface';
import { ExportSummaryReportResponse } from '../interfaces/export-summary-report-response.interface';
import { ExportSummaryReportNcqRow } from '../interfaces/export-summary-report-ncq-row';
import { ExportSummaryReportJobRow } from '../interfaces/export-summary-report-job-row';

import { DataTableConverter } from '../helpers/data-table-converter';

import { AppDataService } from './app-data.service';
import { ExcelService, ExcelWorkSheetCellObject, ExcelWorkSheetCellTransformationFunction } from './excel.service';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
  constructor(
    private appDataService: AppDataService,
    private dialogService: DialogService,
    private excelService: ExcelService) {
  }

  // Exports to XLSX

  exportMaatAuditPlansToXlsx(request: ExportToDataTablesRequest): void {
    this.exportToXlsx(API_ENDPOINTS.maatAuditPlan.exportToDataTables, request, 'MAAT-Audit-Plans');
  }

  exportToXlsx<TRequest extends ExportRequest, TResponse = ExportToDataTablesResponse>(
    apiEndpoint: string,
    request: TRequest,
    fileNamePrefix: string,
    getExportResponse: (response: TResponse) => ExportToDataTablesResponse = null,
    onSuccess: (response: TResponse, fileName: string) => void = (response, fileName) => this.onExportSuccess(response, fileName),
    onError: () => void = () => this.onExportError()): void {

    this.appDataService.getDataTableSet(apiEndpoint, request, (response: TResponse, transientId: string) => {
      const exportResponse = getExportResponse?.(response) ?? response as unknown as ExportToDataTablesResponse;
      try {
        const fileName = this.exportDataTablesToXlsx(exportResponse, fileNamePrefix, transientId);
        onSuccess?.(response, fileName);
      }
      catch (error) {
        console.error(error);
        onError?.();
      }
    });
  }

  exportDataTablesToXlsx(
    exportResponse: ExportToDataTablesResponse,
    fileNamePrefix: string,
    transientId: string = null): string {

    transientId ??= this.appDataService.getTransientId();
    const fileName = `${fileNamePrefix}_${exportResponse?.dataSetFriendlyName}_${transientId}.xlsx`;
    const workSheetInfos = exportResponse
      ?.dataTables
      ?.map(dataTable => ({
        workSheetName: dataTable?.tableName,
        workSheetData: dataTable?.data,
        workSheetCellTransformationFunction: this.getWorkSheetCellTransformationFunction(dataTable)
      }))
      ?? [];
    this.excelService.writeWorkBookToFile(fileName, workSheetInfos);
    return fileName;
  }

  exportMaatAuditPlanSummaryReportToXlsx(request: ExportSummaryReportRequest) {
    this.appDataService.getDataTableSet(
      API_ENDPOINTS.maatAuditPlan.exportSummaryReport,
      request,
      (response: ExportSummaryReportResponse, transiendId: string) => {
        try {
          const workSheetData = this.getSummaryReportWorkSheetData(response);
          const fileName = `Summary-Report_${transiendId}.xlsx`;
          this.excelService.writeWorkBookToFile(fileName, [{
            workSheetName: 'Summary Report',
            workSheetData
          }]);
          this.onExportSuccess(response, fileName);
        }
        catch (error) {
          console.error(error);
          this.onExportError();
        }
      }
    )
  }

  private getSummaryReportWorkSheetData(response: ExportSummaryReportResponse): string[][] {
    const sectionOneDescription =
    [
      ['Order Nos. with defaults and/or breaches where reconciliation has not been achieved (i.e. reasons/explanations provided by the contractor are not accepted by MA&PC).']
    ];

    const sectionTwoDescription =
    [
      ['Order Nos. with defaults and/or breaches where reasons provided in response to the Notice of Non-Conformance have been accepted and/or defects were rectified and/or omitted works were completed and/or charges were refunded.']
    ];

    const sectionThreeDescription =
    [
      ['Order Nos. with late completion where reasons provided are not substantiated and therefore not acceptable.']
    ];

    const mapNcqSectionOneRows = (ncqRows: ExportSummaryReportNcqRow[]) => {
      return (ncqRows ?? []).map(ncqRow => [
        ncqRow.ncqNumber ?? '',
        ncqRow.orderNumber ?? '',
        ncqRow.managerComments ?? '',
        ncqRow.formattedValue ?? '',
        ncqRow.trade ?? '',
        ncqRow.reInspectFee ?? ''
      ]);
    };

    const mapNcqSectionTwoRows = (ncqRows: ExportSummaryReportNcqRow[]) => {
      return (ncqRows ?? []).map(ncqRow => [
        ncqRow.ncqNumber ?? '',
        ncqRow.orderNumber ?? '',
        ncqRow.formattedValue ?? '',
        ncqRow.trade ?? '',
        ncqRow.reInspectFee ?? ''
      ]);
    };

    const mapNcqSectionThreeRows = (ncqRows: ExportSummaryReportNcqRow[]) => {
      return (ncqRows ?? []).map(ncqRow => [
        ncqRow.ncqNumber ?? '',
        ncqRow.orderNumber ?? '',
        ncqRow.managerComments ?? ''
      ]);
    };

    const mapJobSectionFourRows = (jobRows: ExportSummaryReportJobRow[]) => {
      return (jobRows ?? []).map(jobRow => [
        jobRow.scOrderNumber.toString(),
        jobRow.address ?? ''
      ]);
    };

    const sectionOneOverchargeNcqs = mapNcqSectionOneRows(response.sectionOneOverchargeNcqs);
    const sectionOneDefectiveWorksNcqs = mapNcqSectionOneRows(response.sectionOneDefectiveWorksNcqs);
    const sectionTwoOverchargeNcqs = mapNcqSectionTwoRows(response.sectionTwoOverchargeNcqs);
    const sectionTwoDefectiveWorksNcqs = mapNcqSectionTwoRows(response.sectionTwoDefectiveWorksNcqs);
    const sectionThreeLateWorksNcqs = mapNcqSectionThreeRows(response.sectionThreeLateWorksNcqs);
    const sectionFourNoAccessJobs = mapJobSectionFourRows(response.sectionFourNoAccessJobs);

    const sectionOneNcqTableHeadings = ['Ncq No', 'Order No', "MA&PC Manager's Comments", 'Value $', 'Trade', 'Re-inspect Fee Y/N'];
    const sectionTwoNcqTableHeadings = ['Ncq No', 'Order No', 'Value $', 'Trade', 'Re-inspect Fee Y/N'];
    const sectionThreeNcqTableHeadings = ['Ncq No', 'Job No', 'Manager Comments'];
    const sectionFourNoAccessJobTableHeadings = ['SC Order No.', 'Address'];

    return [
      ['Summary of Contractor Perfomance'],
      ['Contractor', response.contractor ?? ''],
      ['Contract No./Contract Area', response.contractNumberAndArea ?? ''],
      ['Compliance Plan No.', response.compliancePlanNumber],
      ['Compliance Summary Dated', response.complianceSummaryDated ?? ''],
      [],
      ['Unresolved Non-Conformance Issues'],
      ...sectionOneDescription,
      [],
      ['1A. Overcharges'],
      sectionOneNcqTableHeadings,
      ...sectionOneOverchargeNcqs,
      [],
      response.sectionOneOverchargeNcqsTotalRow,
      [],
      ['1B. Defective Works'],
      sectionOneNcqTableHeadings,
      ...sectionOneDefectiveWorksNcqs,
      [],
      response.sectionOneDefectiveWorksNcqsTotalRow,
      [],
      ['Sustained and Resolved Non-conformance Issues'],
      ...sectionTwoDescription,
      [],
      ['2A. Overcharges'],
      sectionTwoNcqTableHeadings,
      ...sectionTwoOverchargeNcqs,
      [],
      response.sectionTwoOverchargeNcqsTotalRow,
      [],
      ['2B. Defective Works'],
      sectionTwoNcqTableHeadings,
      ...sectionTwoDefectiveWorksNcqs,
      [],
      response.sectionTwoDefectiveWorksNcqsTotalRow,
      [],
      ['Overcharges'],
      ['1A', response.sectionOneOvercharges],
      ['2A', response.sectionTwoOvercharges],
      ['Total Overcharges', response.totalOvercharges],
      [],
      ['Defective Works'],
      ['1B', response.sectionOneDefectiveWorks],
      ['2B', response.sectionTwoDefectiveWorks],
      ['Total Defective Works', response.totalDefectiveWorks],
      [],
      ['3. Late Works'],
      ...sectionThreeDescription,
      [],
      sectionThreeNcqTableHeadings,
      ...sectionThreeLateWorksNcqs,
      [],
      ['4. SC Orders Removed from Plan (No Access)'],
      sectionFourNoAccessJobTableHeadings,
      ...sectionFourNoAccessJobs,
      [],
      ...response.signOffSection
    ]
  }

  private onExportSuccess<TResponse>(response: TResponse, fileName: string): void {
    this.dialogService.showSimplePopup(`The data has been successfully exported to file "${fileName}".`, 'Export Successful', 'OK');
  }

  private onExportError(): void {
    this.dialogService.showSimplePopup(`An unexpected error occurred while attempting to export the data.`, 'Export Failed', 'OK');
  }

  private static readonly CellTransformationFunctions: Record<DATA_TYPE, (cellObject: ExcelWorkSheetCellObject) => void> = {
    [DATA_TYPE.String]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toString(cellObject.v);
        cellObject.t = EXCEL_CELL_TYPE.String;
        cellObject.z = EXCEL_CELL_FORMAT.Text;
        delete cellObject.w;
      },
    [DATA_TYPE.Boolean]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toBoolean(cellObject.v);
        cellObject.t = EXCEL_CELL_TYPE.Boolean;
        delete cellObject.z;
        delete cellObject.w;
      },
    [DATA_TYPE.Number]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toNumber(cellObject.v);
        cellObject.t = EXCEL_CELL_TYPE.Number;
        delete cellObject.z;
        delete cellObject.w;
      },
    [DATA_TYPE.Money]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toNumber(cellObject.v);
        cellObject.t = EXCEL_CELL_TYPE.Number;
        cellObject.z = EXCEL_CELL_FORMAT.Money;
        delete cellObject.w;
      },
    [DATA_TYPE.Date]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toDate(cellObject.v, LUXON_CELL_FORMAT.Date);
        cellObject.t = EXCEL_CELL_TYPE.Date;
        cellObject.z = EXCEL_CELL_FORMAT.Date;
        delete cellObject.w;
      },
    [DATA_TYPE.Timestamp]:
      (cellObject: ExcelWorkSheetCellObject) => {
        cellObject.v = DataTableConverter.toDate(cellObject.v, LUXON_CELL_FORMAT.DateTime);
        cellObject.t = EXCEL_CELL_TYPE.Date;
        cellObject.z = EXCEL_CELL_FORMAT.Timestamp;
        delete cellObject.w;
      }
  }

  private getWorkSheetCellTransformationFunction(dataTable: DataTable): ExcelWorkSheetCellTransformationFunction {

    const workSheetCellTransformationFunction = (rowIndex: number, columnIndex: number, cellObject: ExcelWorkSheetCellObject) => {

      if (rowIndex === 0) {
        return;
      }
      if (!cellObject || !cellObject.v) {
        return;
      }

      const columnName = dataTable.data[0][columnIndex];
      const dataType = dataTable.dataTypeByColumnName?.[columnName];
      if (!dataType) {
        return;
      }

      const cellTransformationFunction = ExcelExportService.CellTransformationFunctions[dataType];
      if (!cellTransformationFunction) {
        return;
      }

      try {
        cellTransformationFunction(cellObject);
      }
      catch (innerError) {
        const errorData = { tableName: dataTable.tableName, columnName, dataType, rowIndex, columnIndex };
        throw new Error(`Failed to transform cell (${JSON.stringify(errorData)})`, { cause: innerError });
      }
    };

    return workSheetCellTransformationFunction;
  }
}
